import { useState, useRef, useEffect, useContext, useMemo } from "react";
import SingleDropDown from "./SingleDropDown";
import {
  IconButton,
  Popper,
  Tooltip,
  Typography,
  Paper,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useToggle } from "../CustomHooks/useToggle";
import { MdHelpOutline } from "react-icons/md";
import { useStyles } from "../Styles/styles";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import PersonAutoSuggestRefactor from "./PersonAutoSuggestRefactor";
import AddressWithAlertAndHmi from "./AddressWithAlertAndHmi";
import { MutationContext } from "../Contexts/MutationContext";
import { getPoldTrigger } from "../utils/getPoldTrigger";

export const poldAnswers = [
  {
    id: 1,
    fullName: "Yes",
    name: "Y",
    value: true,
  },
  {
    id: 2,
    fullName: "No",
    name: "N",
    value: false,
  },
];

const PoldFields = ({
  incident,
  poldInstance = {},
  poldOptOut = false,
  setPausePolling,
  user,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showPopper, togglePopper] = useToggle(false);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_INCIDENT } = INCIDENT_ACTIONS;
  const { updatePold } = useContext(MutationContext);
  const classes = useStyles();

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const handleChange = event => {
    const { name, value } = event.target;
    if (name === "hasPold") {
      let dispatchValue = { hasPold: value?.value };
      let updateInput = {
        originatorId: user?.id,
        incidentId: incident?.id,
        poldInstanceId: poldInstance?.id,
        hasPold: value?.value,
      };
      if (value?.value === false) {
        dispatchValue = { hasPold: value?.value, business: "", address: "" };
        updateInput = {
          originatorId: user?.id,
          incidentId: incident?.id,
          poldInstanceId: poldInstance?.id,
          hasPold: value?.value,
          businessId: "",
          addressId: "",
        };
      }
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incident?.id,
          property: "poldInstance",
          value: dispatchValue,
        },
      });
      if (user?.id && incident?.id && value?.value !== undefined) {
        updatePold({
          variables: {
            input: updateInput,
          },
        });
      }
    }
  };

  const handlePopperClick = e => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
    togglePopper();
  };

  const handlePoldBusinessSelect = place => {
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incident?.id,
        property: "poldInstance",
        value: { business: place },
      },
    });
    if (user?.id && incident?.id) {
      updatePold({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incident?.id,
            poldInstanceId: poldInstance?.id,
            businessId: place?.id || "",
          },
        },
      });
    }
  };

  const handlePoldBusinessClear = () => {
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incident?.id,
        property: "poldInstance",
        value: { business: "" },
      },
    });
    if (user?.id && incident?.id) {
      updatePold({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incident?.id,
            poldInstanceId: poldInstance?.id,
            businessId: "",
          },
        },
      });
    }
  };

  const handlePoldAddressSelect = address => {
    incidentDispatch({
      type: UPDATE_INCIDENT,
      payload: {
        id: incident?.id,
        property: "poldInstance",
        value: { address },
      },
    });
    if (user?.id && incident?.id) {
      updatePold({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incident?.id,
            poldInstanceId: poldInstance?.id,
            addressId: address?.id || "",
          },
        },
      });
    }
  };

  const showPold = useMemo(() => {
    return getPoldTrigger({ incident, poldOptOut }).showPold;
  }, [incident, poldOptOut]);

  if (showPold) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            border: "1px solid black",
            borderRadius: "5px",
            padding: "0 10px",
          }}
        >
          <>
            <SingleDropDown
              title={"Licensed POLD?"}
              value={
                poldAnswers.find(pA => pA.value === poldInstance?.hasPold) || ""
              }
              stateName={"hasPold"}
              options={poldAnswers}
              handleChange={handleChange}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              disabled={!props.canEdit}
              name={"fullName"}
              showBlank={false}
            />
            <Tooltip title="Click for the POLD description">
              <IconButton
                style={{
                  ...{
                    padding: "0px",
                    width: "30px",
                    height: "30px",
                    fontSize: "20px",
                  },
                }}
                onClick={handlePopperClick}
              >
                <MdHelpOutline />
              </IconButton>
            </Tooltip>
          </>
          {poldInstance?.hasPold && (
            <>
              <PersonAutoSuggestRefactor
                person={poldInstance?.business}
                business={true}
                userRoles={user?.roles}
                userId={user?.id}
                dispatch={incidentDispatch}
                action={UPDATE_INCIDENT}
                handleAddPerson={handlePoldBusinessSelect}
                incidentId={incident?.id}
                containerStyle={{}}
                setPausePolling={setPausePolling}
                {...props}
              />
              <button onClick={handlePoldBusinessClear}>Clear</button>
              <p style={{ padding: "0 10px" }}>-OR-</p>
              <AddressWithAlertAndHmi
                userRoles={user?.roleNames}
                address={poldInstance?.address}
                showHmi={false}
                showAlert={false}
                showPeople={false}
                addressInputLabel={"POLD\xa0Address"}
                setAddress={handlePoldAddressSelect}
                incidentId={incident?.id}
                onFocus={() => setPausePolling && setPausePolling(true)}
                onBlur={() => setPausePolling && setPausePolling(false)}
                showIntersection={false}
                {...props}
              />
            </>
          )}
        </div>
        <Popper
          placement="right"
          open={showPopper}
          disablePortal={true}
          anchorEl={anchorEl}
          modifiers={{
            flip: {
              enabled: false,
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: "viewport",
            },
          }}
          style={{ zIndex: 1400 }}
        >
          <Paper
            style={{
              padding: 10,
              border: "3px solid grey",
              boxShadow: "1px 1px 1px grey",
            }}
          >
            <Typography>
              <CloseIcon
                style={{ float: "right" }}
                onClick={handlePopperClick}
                className={classes.hover}
              />
              Did your investigation determine that the place of last drink
              <br />
              was a licensed liquor establishment or permitted event?
            </Typography>
          </Paper>
        </Popper>
      </div>
    );
  } else {
    return null;
  }
};

export default PoldFields;
